import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import logo from '../../assets/merchanerp-logo.png';
import { authApi } from '../../api/auth';
import { useToast } from '../../hooks/useToast';
const schema = z.object({ company_name: z.string().min(2), currency_code: z.string().min(3), name: z.string().min(2), email: z.string().email(), password: z.string().min(8), password_confirmation: z.string().min(8) }).refine(d => d.password === d.password_confirmation, { message: 'Passwords do not match', path: ['password_confirmation'] });
type FormData = z.infer<typeof schema>;
const CURRENCIES = [{ code: 'USD', name: 'US Dollar' }, { code: 'SOS', name: 'Somali Shilling' }, { code: 'KES', name: 'Kenyan Shilling' }, { code: 'ETB', name: 'Ethiopian Birr' }, { code: 'AED', name: 'UAE Dirham' }, { code: 'EUR', name: 'Euro' }, { code: 'GBP', name: 'British Pound' }];
const Register: React.FC = () => {
  const navigate = useNavigate(); const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { currency_code: 'USD' } });
  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await authApi.register({ ...data, timezone: 'Africa/Mogadishu' });
      setDone(true);
      showToast('Registration received — pending approval.');
    } catch (err: any) { showToast(err.response?.data?.message || 'Registration failed', 'error'); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="auth-layout">
        <div className="auth-card" style={{ maxWidth: 440, textAlign: 'center' }}>
          <div className="auth-logo"><img src={logo} alt="MerchanERP" style={{ height: 34 }} /></div>
          <div style={{ fontSize: 40, lineHeight: 1, margin: '8px 0 14px' }}>✓</div>
          <div className="auth-title">Thanks — you're registered</div>
          <div className="auth-subtitle" style={{ marginTop: 10 }}>
            Your company is <b>awaiting approval</b>. We'll review it and switch you on — you'll be able to sign in once it's approved.
          </div>
          <Link to="/login" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 20, textDecoration: 'none' }}>Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-logo"><img src={logo} alt="MerchanERP" style={{ height: 34 }} /></div>
        <div className="auth-title">Create your company</div>
        <div className="auth-subtitle">Register in a few minutes — we'll review and activate it.</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Company Name</label><input className="form-control" placeholder="Auto Parts Ltd" {...register('company_name')} />{errors.company_name && <div className="form-error">{errors.company_name.message}</div>}</div>
            <div className="form-group"><label className="form-label">Base Currency</label><select className="form-control" {...register('currency_code')}>{CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}</select></div>
          </div>
          <div className="form-group"><label className="form-label">Your Name</label><input className="form-control" placeholder="Your name" {...register('name')} />{errors.name && <div className="form-error">{errors.name.message}</div>}</div>
          <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" placeholder="you@company.com" {...register('email')} />{errors.email && <div className="form-error">{errors.email.message}</div>}</div>
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Password</label><input className="form-control" type="password" {...register('password')} />{errors.password && <div className="form-error">{errors.password.message}</div>}</div>
            <div className="form-group"><label className="form-label">Confirm</label><input className="form-control" type="password" {...register('password_confirmation')} />{errors.password_confirmation && <div className="form-error">{errors.password_confirmation.message}</div>}</div>
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={loading}>{loading ? 'Submitting...' : 'Register company'}</button>
        </form>
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 12, color: 'var(--tx3)' }}>Already have an account? <Link to="/login" style={{ color: 'var(--blue)', fontWeight: 600 }}>Sign in</Link></div>
      </div>
    </div>
  );
};
export default Register;
